import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  createBaseSelector,
  registerSlice,
  StatusType
} from '@shared/lib/redux'

import { PlanModel } from '../domain/plan.model'
import { PaymentMethodType } from '../types'

export interface PlanStore {
  data: PlanModel[]
  paymentMethodList: PaymentMethodType[]
  status: StatusType
  error: string | null
}

const initialState: PlanStore = {
  data: [],
  paymentMethodList: [],
  status: 'idle',
  error: null
}

const planSlice = createSlice({
  name: 'planSlice',
  initialState,
  reducers: {
    getPlanList(state) {
      state.status = 'loading'
    },
    getPlanListSuccess(state, action: PayloadAction<PlanModel[]>) {
      state.data = action.payload
      state.status = 'success'
      state.error = null
    },
    getPlanListFailure(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },
    getPaymentMethodList(state) {
      state.status = 'loading'
    },
    getPaymentMethodListSuccess(
      state,
      action: PayloadAction<PaymentMethodType[]>
    ) {
      state.paymentMethodList = action.payload
      state.status = 'success'
      state.error = null
    },
    getPaymentMethodListFailure(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },
    reset(state) {
      state.data = []
      state.status = 'idle'
      state.error = null
    }
  }
})

const planBaseSelector = createBaseSelector(planSlice)
registerSlice([planSlice])

const selectPlanList = createSelector(planBaseSelector, (s) => {
  return {
    data: s.data,
    status: s.status,
    error: s.error
  }
})

const selectStatus = createSelector(planBaseSelector, (s) => {
  return {
    status: s.status,
    error: s.error
  }
})

const selectCurrencies = createSelector(planBaseSelector, (s) => {
  return {
    currencies: s.paymentMethodList.reduce<{ label: string; value: string }[]>(
      (acc, curr) => {
        if (!acc.some((item) => item.value === curr.currency)) {
          acc.push({ label: curr.currency, value: curr.currency })
        }
        return acc
      },
      []
    )
  }
})

const selectPaymentNetwork = createSelector(
  planBaseSelector,
  (_: unknown, selectedCurrency: string | null) => selectedCurrency,
  (s, selectedCurrency) => {
    if (!selectedCurrency) return { networks: [] }
    return {
      networks: s.paymentMethodList
        .filter((p) => p.currency === selectedCurrency)
        .map((p) => ({
          label: p.network,
          value: p.network
        }))
    }
  }
)

export const planStore = {
  actions: { ...planSlice.actions },
  selectors: {
    selectPlanList,
    selectStatus,
    selectCurrencies,
    selectPaymentNetwork
  },
  name: planSlice.name
}
